/**
 * Hardware abstraction boundary. The rest of the system — transport, control
 * mapping, UI, safety — depends ONLY on this interface. Implementations are
 * selected by config (`actuator.type`); no hardware type may leak upward.
 */

export const ARM_JOINTS = ['boom', 'dipper', 'bucket'] as const;
export type ArmJoint = (typeof ARM_JOINTS)[number];

export interface ActuatorCapabilities {
  /** Differential (tank) drive is the only drive model supported today. */
  readonly drive: 'differential';
  /** Whether the platform has cab slew independent of the tracks. */
  readonly slew: boolean;
  /** Arm joints this actuator can move. */
  readonly joints: readonly ArmJoint[];
}

/**
 * All rate inputs are normalised to -1.0 .. 1.0. Implementations must clamp
 * defensively and treat NaN as 0. Calls are level-triggered state, not
 * events: the latest call wins, and callers re-send state every tick.
 */
export interface DiggerActuator {
  /** Tank/differential drive. left/right: -1 full reverse .. 1 full forward. */
  drive(left: number, right: number): void;
  /** Cab yaw / slew. -1 full left .. 1 full right. */
  rotate(rate: number): void;
  /** Move one arm joint. -1 .. 1; sign convention documented per joint. */
  arm(joint: ArmJoint, rate: number): void;
  /** Immediate all-stop. Must be safe to call repeatedly and at any time. */
  stop(): void;
  readonly capabilities: ActuatorCapabilities;
}

/** Clamp to [-1, 1]; NaN and non-finite become 0. */
export function clampRate(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.min(1, Math.max(-1, v));
}
